import { readsCampaignsAsStaff, staffCampaigns } from '@/lib/adminApi';
import { useEffect, useMemo, useState } from 'react';
import { Check, Sparkles, Clock, Layers, AlertTriangle, PauseCircle, Package } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/lib/supabase';
import type { WizardMode, CampaignTypeKey } from '@/stores/campaignWizardStore';
import { useWizardStore } from '@/stores/campaignWizardStore';
import {
  CAMPAIGN_TYPES,
  CAMPAIGN_TYPE_GROUPS,
  getCampaignTypeByKey,
  type CampaignTypeDef,
} from '@/lib/campaignTypeRegistry';
import { CAMPAIGN_PACKAGES, type CampaignPackage } from '@/lib/campaignPackages';

/**
 * Minimal shape we need from `ad_campaigns` to flag duplicates per
 * template card. Only fetched fields land here.
 */
interface ExistingCampaignSummary {
  id: string;
  slug: string;
  placement: string;
  active: boolean;
  is_paused: boolean | null;
  starts_at: string | null;
  ends_at: string | null;
}

/** "Live" = active, not paused, and currently inside its date window. */
function classifyCampaign(c: ExistingCampaignSummary): 'live' | 'paused' | 'scheduled' | 'expired' {
  const now = Date.now();
  const startsMs = c.starts_at ? Date.parse(c.starts_at) : null;
  const endsMs = c.ends_at ? Date.parse(c.ends_at) : null;
  if (endsMs != null && endsMs < now) return 'expired';
  if (!c.active) return 'paused';
  if (c.is_paused) return 'paused';
  if (startsMs != null && startsMs > now) return 'scheduled';
  return 'live';
}

interface CampaignTypePickerProps {
  mode: WizardMode;
  /** Locks the picker to a specific type (e.g. Listings tab → map_pin). */
  lockedTypeKey?: string;
}

function TemplateCard({
  def,
  isSelected,
  isLocked,
  onToggle,
  existingCampaigns,
}: {
  def: CampaignTypeDef;
  isSelected: boolean;
  isLocked: boolean;
  onToggle: () => void;
  /** Brand's existing campaigns whose placement matches this card. */
  existingCampaigns: ExistingCampaignSummary[];
}) {
  // Bucket existing campaigns so we can show a clear "1 live · 1 paused"
  // hint instead of just a raw count. Operators care most about LIVE
  // ones (those would auction against the new campaign immediately)
  // and least about expired ones (informational only).
  const buckets = useMemo(() => {
    const out = { live: 0, paused: 0, scheduled: 0, expired: 0 };
    for (const c of existingCampaigns) {
      out[classifyCampaign(c)] += 1;
    }
    return out;
  }, [existingCampaigns]);
  const hasActiveOverlap = buckets.live > 0 || buckets.scheduled > 0;
  const hasAnyOverlap = hasActiveOverlap || buckets.paused > 0;
  // Border + glow tone reflects severity: red for live overlap, amber
  // for paused-only, default otherwise. Selected state still wins for
  // the green emerald border (operator has chosen, they know).
  const overlapBorderClass = hasActiveOverlap
    ? 'border-red-500/60 bg-red-500/5'
    : buckets.paused > 0
      ? 'border-amber-500/40 bg-amber-500/5'
      : 'border-gray-700/60 bg-gray-800/20';

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={isLocked && isSelected}
      className={`group relative text-left p-5 rounded-2xl border-2 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${
        isSelected
          ? 'border-emerald-500 bg-emerald-500/10 shadow-lg shadow-emerald-900/30'
          : `${overlapBorderClass} hover:border-gray-600 hover:bg-gray-800/40`
      }`}
    >
      <div
        className={`absolute top-3.5 right-3.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
          isSelected
            ? 'bg-emerald-500 border-emerald-500'
            : 'border-gray-600 bg-transparent group-hover:border-gray-400'
        }`}
      >
        {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
      </div>

      <div className="flex items-start gap-3 pr-7 mb-2.5">
        <span className="text-3xl leading-none flex-shrink-0">{def.icon}</span>
        <div className="flex-1 min-w-0">
          <p className={`font-bold text-base leading-tight ${isSelected ? 'text-emerald-300' : 'text-white'}`}>
            {def.label}
          </p>
          <p className={`text-[10px] uppercase tracking-wider font-semibold mt-1 ${isSelected ? 'text-emerald-500' : 'text-gray-500'}`}>
            {def.tagline}
          </p>
        </div>
      </div>

      <p className="text-xs text-gray-400 leading-relaxed mb-3">{def.description}</p>

      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge
          variant="outline"
          className={`text-[10px] px-2 py-0.5 whitespace-nowrap ${isSelected ? 'border-emerald-500/40 text-emerald-300' : 'border-gray-700 text-gray-400'}`}
        >
          {def.aspectRatioLabel}
        </Badge>
        <Badge
          variant="outline"
          className={`text-[10px] px-2 py-0.5 flex items-center gap-1 whitespace-nowrap ${isSelected ? 'border-emerald-500/40 text-emerald-300' : 'border-gray-700 text-gray-400'}`}
        >
          <Sparkles className="w-2.5 h-2.5" />
          ~{def.estimatedDailyImpressions.toLocaleString()}/day
        </Badge>
        {def.defaultHourEnd - def.defaultHourStart < 24 && (
          <Badge
            variant="outline"
            className={`text-[10px] px-2 py-0.5 flex items-center gap-1 whitespace-nowrap ${isSelected ? 'border-amber-500/40 text-amber-300' : 'border-amber-800/40 text-amber-600'}`}
          >
            <Clock className="w-2.5 h-2.5" />
            {def.defaultHourStart}–{def.defaultHourEnd}h
          </Badge>
        )}
      </div>

      {def.goodFor.length > 0 && (
        <p className="text-[10px] text-gray-600 mt-2.5 leading-relaxed">
          <span className="text-gray-500 font-medium">Good for:</span>{' '}
          {def.goodFor.join(' · ')}
        </p>
      )}

      {/* Duplicate-overlap warning strip. Renders only when this brand
          already has ≥1 campaign on this exact placement. Hover for the
          list of overlapping campaigns + their statuses. Pointer-events
          on the tooltip trigger are intentionally NOT swallowed so the
          card stays clickable — the tooltip opens on focus/hover only. */}
      {hasAnyOverlap && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              onClick={(e) => e.stopPropagation()}
              className={`mt-3 flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-[11px] leading-snug ${
                hasActiveOverlap
                  ? 'border-red-500/40 bg-red-500/10 text-red-100'
                  : 'border-amber-500/40 bg-amber-500/10 text-amber-100'
              }`}
              role="status"
            >
              {hasActiveOverlap ? (
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-red-300" />
              ) : (
                <PauseCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-300" />
              )}
              <div className="flex-1 min-w-0">
                <span className="font-semibold">
                  {hasActiveOverlap
                    ? 'This brand already has a campaign here'
                    : 'This brand has a paused campaign here'}
                </span>
                <span className="opacity-80">
                  {' · '}
                  {(() => {
                    const parts: string[] = [];
                    if (buckets.live > 0) parts.push(`${buckets.live} live`);
                    if (buckets.scheduled > 0) parts.push(`${buckets.scheduled} scheduled`);
                    if (buckets.paused > 0) parts.push(`${buckets.paused} paused`);
                    if (buckets.expired > 0 && parts.length === 0) parts.push(`${buckets.expired} expired`);
                    return parts.join(' · ');
                  })()}
                </span>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <div className="space-y-1.5">
              <p className="font-semibold text-xs">
                {existingCampaigns.length} existing campaign
                {existingCampaigns.length === 1 ? '' : 's'} on this placement:
              </p>
              <ul className="text-[11px] space-y-0.5 max-h-40 overflow-y-auto pr-1">
                {existingCampaigns.slice(0, 8).map((c) => {
                  const status = classifyCampaign(c);
                  return (
                    <li key={c.id} className="flex items-center gap-1.5">
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          status === 'live'
                            ? 'bg-red-400'
                            : status === 'scheduled'
                              ? 'bg-blue-400'
                              : status === 'paused'
                                ? 'bg-amber-400'
                                : 'bg-gray-500'
                        }`}
                      />
                      <span className="font-mono truncate">{c.slug}</span>
                      <span className="ml-auto text-[10px] uppercase opacity-70">{status}</span>
                    </li>
                  );
                })}
                {existingCampaigns.length > 8 && (
                  <li className="opacity-60 italic">…and {existingCampaigns.length - 8} more</li>
                )}
              </ul>
              <p className="text-[10px] italic opacity-80 pt-1">
                {hasActiveOverlap
                  ? 'Creating another will auction against the existing one and probably waste impressions. Consider editing the existing campaign on the Sponsors page instead.'
                  : 'Reactivating a paused campaign keeps its history, slug, and contract.'}
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </button>
  );
}

export default function CampaignTypePicker({ mode, lockedTypeKey }: CampaignTypePickerProps) {
  const store = useWizardStore(mode);
  const { campaignTypes, selectedBusiness, packageKey } = store;

  const filtered: CampaignTypeDef[] = CAMPAIGN_TYPES.filter((t) => {
    if (mode === 'festival') return t.isFestival === true;
    if (mode === 'listing') return t.key === 'sponsored_map_pin';
    return t.isFestival !== true;
  });

  // ── Existing-campaign lookup for duplicate warnings ──────────────
  // Pull every campaign the brand already has on file so the template
  // cards can flag overlaps before the operator wastes time entering
  // a duplicate. We match on listing_id (the new linkage column added
  // in 20260621210000) AND on brand_name (legacy campaigns that
  // predate the column) so the warning catches both shapes.
  const [existingByPlacement, setExistingByPlacement] = useState<
    Record<string, ExistingCampaignSummary[]>
  >({});
  const [existingLoading, setExistingLoading] = useState(false);

  useEffect(() => {
    if (!selectedBusiness) {
      setExistingByPlacement({});
      return;
    }
    let cancelled = false;
    setExistingLoading(true);
    (async () => {
      try {
        // OR filter: either the campaign's listing_id matches OR its
        // brand_name does. We can't pass both predicates to .or() if
        // they touch different columns with different operators, but
        // PostgREST does support `.or(listing_id.eq.X,brand_name.eq.Y)`.
        const safeName = selectedBusiness.name.replace(/[(),]/g, ''); // PostgREST or()-friendly
        const { data, error } = readsCampaignsAsStaff()
          ? await staffCampaigns({ listingId: selectedBusiness.id, brandName: selectedBusiness.name, limit: 50 })
              .then((res) => ({ data: res.rows, error: null }))
          : await supabase
            .from('ad_campaigns')
            .select('id, slug, placement, active, is_paused, starts_at, ends_at')
            .or(`listing_id.eq.${selectedBusiness.id},brand_name.eq.${safeName}`)
            .order('created_at', { ascending: false })
            .limit(50);
        if (cancelled) return;
        if (error) throw error;
        const grouped: Record<string, ExistingCampaignSummary[]> = {};
        (data as ExistingCampaignSummary[] | null)?.forEach((row) => {
          if (!row.placement) return;
          if (!grouped[row.placement]) grouped[row.placement] = [];
          grouped[row.placement].push(row);
        });
        setExistingByPlacement(grouped);
      } catch {
        // Non-fatal — the wizard still works without the warnings.
        setExistingByPlacement({});
      } finally {
        if (!cancelled) setExistingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBusiness?.id, selectedBusiness?.name]);

  // Surface a single high-level summary at the top so the operator
  // sees "Best Price has 3 live campaigns" before they start picking,
  // even if they end up on a clean (non-overlapping) template card.
  const totalSummary = useMemo(() => {
    let live = 0;
    let paused = 0;
    let scheduled = 0;
    for (const list of Object.values(existingByPlacement)) {
      for (const c of list) {
        const s = classifyCampaign(c);
        if (s === 'live') live += 1;
        else if (s === 'paused') paused += 1;
        else if (s === 'scheduled') scheduled += 1;
      }
    }
    return { live, paused, scheduled };
  }, [existingByPlacement]);

  const applyPrimaryDefaults = (key: CampaignTypeKey | '') => {
    if (!key) {
      store.set({
        campaignType: '',
        templateKey: '',
        templatePlacement: '',
        templateLabel: '',
        estimatedDailyImpressions: 400,
      });
      return;
    }
    const def = getCampaignTypeByKey(key);
    if (!def) return;
    store.set({
      campaignType: def.key as CampaignTypeKey,
      templateKey: def.templateKey,
      templatePlacement: def.placement,
      templateLabel: def.label,
      estimatedDailyImpressions: def.estimatedDailyImpressions,
      hourStart: def.defaultHourStart,
      hourEnd: def.defaultHourEnd,
      audience: def.audienceHint,
      festivalMode: def.isFestival === true,
    });
  };

  const toggle = (def: CampaignTypeDef) => {
    if (lockedTypeKey === def.key && campaignTypes.includes(def.key as CampaignTypeKey)) return;

    const isSelected = campaignTypes.includes(def.key as CampaignTypeKey);
    const next = isSelected
      ? campaignTypes.filter((k) => k !== def.key)
      : [...campaignTypes, def.key as CampaignTypeKey];

    store.set({ campaignTypes: next, packageKey: null });
    applyPrimaryDefaults(next[0] || '');
  };

  const applyPackage = (pkg: CampaignPackage) => {
    const start = store.startDate || new Date().toISOString().split('T')[0];
    store.set({
      campaignTypes: [...pkg.campaignTypes],
      packageKey: pkg.key,
      durationDays: pkg.days,
      startDate: start,
      endDate: new Date(new Date(start).getTime() + pkg.days * 86400000)
        .toISOString()
        .split('T')[0],
      invoiceAmountXcd: String(pkg.priceXcd),
    });
    applyPrimaryDefaults(pkg.campaignTypes[0]);
  };

  const showPackages = mode === 'sponsor' && !lockedTypeKey;

  useEffect(() => {
    if (campaignTypes.length > 0) return;
    if (mode === 'listing') {
      const def = getCampaignTypeByKey('sponsored_map_pin');
      if (def) {
        store.set({ campaignTypes: [def.key as CampaignTypeKey] });
        applyPrimaryDefaults(def.key as CampaignTypeKey);
      }
    } else if (mode === 'festival') {
      const def = getCampaignTypeByKey('festival_takeover');
      if (def) {
        store.set({ campaignTypes: [def.key as CampaignTypeKey] });
        applyPrimaryDefaults(def.key as CampaignTypeKey);
      }
    } else if (lockedTypeKey) {
      const def = getCampaignTypeByKey(lockedTypeKey);
      if (def) {
        store.set({ campaignTypes: [def.key as CampaignTypeKey] });
        applyPrimaryDefaults(def.key as CampaignTypeKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, lockedTypeKey]);

  const selectedCount = campaignTypes.length;

  const groupsToShow =
    mode === 'festival'
      ? CAMPAIGN_TYPE_GROUPS.filter((g) => g.id === 'festival')
      : CAMPAIGN_TYPE_GROUPS.filter((g) => g.id !== 'festival');

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-white">Choose templates</h2>
            <p className="text-sm text-gray-400 mt-1">
              Pick one or more placements. Each becomes its own campaign row, sharing brand,
              creative, and schedule.
              {selectedBusiness ? ` For ${selectedBusiness.name}.` : ''}
            </p>
          </div>
          {selectedCount > 0 && (
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 px-3 py-1 text-xs">
              <Layers className="w-3 h-3 mr-1.5" />
              {selectedCount} template{selectedCount === 1 ? '' : 's'} selected
            </Badge>
          )}
        </div>
      </div>

      {showPackages && (
        <section className="space-y-3">
          <div className="px-0.5">
            <h3 className="text-sm font-bold text-gray-200 flex items-center gap-1.5">
              <Package className="w-4 h-4 text-emerald-400" /> Start from a package
            </h3>
            <p className="text-xs mt-0.5 text-gray-500">
              One click fills the placements, dates and a suggested price from the rate card.
              You can still change anything below.
            </p>
          </div>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {CAMPAIGN_PACKAGES.map((pkg) => {
              const active = packageKey === pkg.key;
              return (
                <button
                  key={pkg.key}
                  type="button"
                  onClick={() => applyPackage(pkg)}
                  aria-pressed={active}
                  className={`relative flex flex-col text-left p-4 rounded-2xl border-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${
                    active
                      ? 'border-emerald-500 bg-emerald-500/10 shadow-lg shadow-emerald-900/30'
                      : pkg.highlight
                        ? 'border-emerald-700/50 bg-gray-800/30 hover:border-emerald-600'
                        : 'border-gray-700/60 bg-gray-800/20 hover:border-gray-600 hover:bg-gray-800/40'
                  }`}
                >
                  {pkg.highlight && !active && (
                    <span className="absolute -top-2.5 left-4 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                      Best value
                    </span>
                  )}
                  <div className="flex items-baseline justify-between gap-2">
                    <p className={`font-bold ${active ? 'text-emerald-300' : 'text-white'}`}>{pkg.name}</p>
                    {active && <Check className="w-4 h-4 text-emerald-400 shrink-0" strokeWidth={3} />}
                  </div>
                  <p className="mt-1 text-lg font-bold text-white tabular-nums">
                    EC${pkg.priceXcd.toLocaleString()}
                    <span className="ml-1 text-xs font-medium text-gray-400">/ {pkg.days} days</span>
                  </p>
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">{pkg.pitch}</p>
                  <p className="mt-2 text-[10px] text-gray-500">
                    {pkg.campaignTypes.length} placement{pkg.campaignTypes.length === 1 ? '' : 's'}
                    {' · '}
                    {pkg.goodFor.join(' · ')}
                  </p>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Brand-level history banner. Surfaces immediately so the
          operator knows what's already in flight for this brand before
          they invest time configuring duplicate creative + targeting.
          Per-template cards still show their own per-placement warning
          for the granular case. */}
      {selectedBusiness && !existingLoading && (
        totalSummary.live + totalSummary.scheduled + totalSummary.paused > 0
      ) && (
        <div
          className={`rounded-xl border px-3.5 py-2.5 flex items-start gap-2.5 ${
            totalSummary.live + totalSummary.scheduled > 0
              ? 'border-red-500/40 bg-red-500/10'
              : 'border-amber-500/40 bg-amber-500/10'
          }`}
        >
          <AlertTriangle
            className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
              totalSummary.live + totalSummary.scheduled > 0
                ? 'text-red-300'
                : 'text-amber-300'
            }`}
          />
          <div className="flex-1 min-w-0 text-xs leading-relaxed">
            <p
              className={`font-semibold ${
                totalSummary.live + totalSummary.scheduled > 0
                  ? 'text-red-100'
                  : 'text-amber-100'
              }`}
            >
              {selectedBusiness.name} already has campaigns in the system
            </p>
            <p
              className={`mt-0.5 ${
                totalSummary.live + totalSummary.scheduled > 0
                  ? 'text-red-200/80'
                  : 'text-amber-200/80'
              }`}
            >
              {[
                totalSummary.live > 0 && `${totalSummary.live} live`,
                totalSummary.scheduled > 0 && `${totalSummary.scheduled} scheduled`,
                totalSummary.paused > 0 && `${totalSummary.paused} paused`,
              ]
                .filter(Boolean)
                .join(' · ')}
              {'. '}
              Templates that overlap with an existing campaign for this brand are flagged below. Consider editing the existing one on the Sponsors page instead of creating a duplicate.
            </p>
          </div>
        </div>
      )}

      {groupsToShow.map((group) => {
        const defs = filtered.filter((t) => t.group === group.id);
        if (defs.length === 0) return null;

        const isSheetGroup = group.id === 'sheet';

        return (
          <section key={group.id} className="space-y-3">
            <div
              className={
                isSheetGroup
                  ? 'rounded-xl border border-sky-500/35 bg-sky-950/25 px-4 py-3'
                  : 'px-0.5'
              }
            >
              <h3 className={`text-sm font-bold ${isSheetGroup ? 'text-sky-200' : 'text-gray-200'}`}>
                {group.title}
              </h3>
              <p className={`text-xs mt-0.5 ${isSheetGroup ? 'text-sky-200/70' : 'text-gray-500'}`}>
                {group.subtitle}
              </p>
            </div>

            <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
              {defs.map((def) => (
                <TemplateCard
                  key={def.key}
                  def={def}
                  isSelected={campaignTypes.includes(def.key as CampaignTypeKey)}
                  isLocked={lockedTypeKey === def.key}
                  onToggle={() => toggle(def)}
                  existingCampaigns={existingByPlacement[def.placement] ?? []}
                />
              ))}
            </div>
          </section>
        );
      })}

      {selectedCount > 0 && (
        <div className="bg-emerald-500/8 border border-emerald-500/25 rounded-xl p-3 flex items-start gap-2.5">
          <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-emerald-200 font-semibold">
              {selectedCount === 1
                ? `1 template selected: ${getCampaignTypeByKey(campaignTypes[0])?.label || ''}`
                : `${selectedCount} templates selected. They will publish as ${selectedCount} separate campaigns`}
            </p>
            <p className="text-[11px] text-emerald-200/70 mt-0.5">
              All selected placements share the same creative and schedule. The contract amount
              is split between them so reports add up to the deal total. The first selection
              drives the live preview defaults.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
